import { api } from '@verifime/api-definition';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePasswordless } from '@verifime/cognito-passwordless-auth';

export type TUserState =
  | { status: 'loading' }
  | { status: 'error'; errorDetail?: string }
  | { status: 'authenticated'; isAdmin: boolean; isClient: boolean };

interface UserContextValue {
  state: TUserState;
}

type TTokensParsed = ReturnType<typeof usePasswordless>['tokensParsed'];

interface UserContextProviderProps {
  tokens?: TTokensParsed;
  children: React.ReactNode;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider: React.FC<UserContextProviderProps> = ({ tokens, children }) => {
  const [state, setState] = useState<TUserState>({ status: 'loading' });

  const loadUser = async () => {
    setState({ status: 'loading' });
    try {
      const userMe = await api.getV1userme();
      const hasAdminRole = userMe.roles.includes('admin');
      const hasClientRole = userMe.roles.includes('client');

      setState({ status: 'authenticated', isAdmin: hasAdminRole, isClient: hasClientRole });
    } catch (err) {
      setState({
        status: 'error',
        errorDetail: err instanceof Error ? err.message : 'Failed to load user',
      });
    }
  };

  useEffect(() => {
    if (new Date() < tokens?.expireAt) {
      loadUser();
    } else {
      setState({ status: 'loading' });
    }
  }, [tokens]);

  return (
    <UserContext.Provider
      value={{
        state,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
